import React, { useState } from 'react';
import {
  Box,
  Avatar,
  Typography,
  TextField,
  Button,
  Paper,
  InputBase,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import CommentCardActions from './CommentCardActions';

function getInitials(firstName, lastName) {
  const f = firstName ? firstName.charAt(0).toUpperCase() : '';
  const l = lastName ? lastName.charAt(0).toUpperCase() : '';
  return f + l || '?';
}

function formatRelativeTime(isoString, t) {
  try {
    const now = Date.now();
    const then = new Date(isoString).getTime();
    const diffMs = now - then;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return t('time_just_now');
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return t('time_minutes_short', { count: diffMin });
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return t('time_hours_short', { count: diffHour });
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay === 1) return t('time_yesterday');
    return t('time_days_ago', { count: diffDay });
  } catch {
    return '';
  }
}

/**
 * CommentCard — Google Docs–style comment card.
 *
 * Collapsed (isActive=false): compact 1-line preview, no interactions.
 * Expanded (isActive=true): full text + replies + actions.
 */
const CommentCard = ({
  comment,
  currentUser,
  isActive,
  onActivate,
  onResolve,
  onDelete,
  onEdit,
  onAddReply,
  onDeleteReply,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [replyText, setReplyText] = useState('');

  if (!comment) return null;

  const isSystem = comment.is_system || false;
  const isOwner = !isSystem && currentUser && comment.user && comment.user.id === currentUser.id;
  const replyCount = (comment.replies || []).length;
  const preview = comment.text ? comment.text.slice(0, 72) + (comment.text.length > 72 ? '…' : '') : '';
  const goldAccent = '#c9a96e';

  const handleStartEdit = () => {
    setEditText(comment.text);
    setIsEditing(true);
  };

  const handleSubmitEdit = () => {
    if (editText.trim()) onEdit(editText.trim());
    setIsEditing(false);
  };

  const handleReplySubmit = () => {
    if (replyText.trim()) {
      onAddReply(replyText.trim());
      setReplyText('');
    }
  };

  // ── Collapsed card ───────────────────────────────────────────────────────────
  if (!isActive) {
    return (
      <Paper
        elevation={0}
        onClick={onActivate}
        sx={{
          width: '100%',
          borderRadius: '12px',
          bgcolor: comment.resolved ? 'action.hover' : 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          p: '8px 10px',
          boxSizing: 'border-box',
          cursor: 'pointer',
          opacity: comment.resolved ? 0.6 : 1,
          transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
          '&:hover': {
            borderColor: 'warning.light',
            boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
          },
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
        }}
      >
        <Avatar
          sx={{
            width: 22,
            height: 22,
            fontSize: '0.6rem',
            bgcolor: isSystem ? goldAccent : (comment.user?.color || 'primary.main'),
            flexShrink: 0,
          }}
        >
          {isSystem ? <AutoAwesomeIcon sx={{ fontSize: 14 }} /> : getInitials(comment.user?.first_name, comment.user?.last_name)}
        </Avatar>

        <Typography
          variant="caption"
          sx={{
            flex: 1,
            color: comment.resolved ? 'text.disabled' : 'text.secondary',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            lineHeight: 1.4,
            fontSize: '0.75rem',
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: isSystem ? goldAccent : 'text.primary', mr: 0.5 }}>
            {isSystem ? 'Lawis AI' : comment.user?.first_name}
          </Box>
          {preview}
        </Typography>

        {replyCount > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              fontSize: '0.7rem',
              flexShrink: 0,
              bgcolor: 'primary.50',
              px: 0.75,
              py: 0.25,
              borderRadius: '10px',
            }}
          >
            {replyCount}
          </Typography>
        )}
      </Paper>
    );
  }

  // ── Expanded (active) card ────────────────────────────────────────────────────
  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: '12px',
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: isSystem ? goldAccent : 'warning.main',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        p: 1.5,
        boxSizing: 'border-box',
        opacity: comment.resolved ? 0.7 : 1,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: '0.7rem',
              bgcolor: isSystem ? goldAccent : (comment.user?.color || 'primary.main'),
              flexShrink: 0,
            }}
          >
            {isSystem ? <AutoAwesomeIcon sx={{ fontSize: 16 }} /> : getInitials(comment.user?.first_name, comment.user?.last_name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isSystem ? goldAccent : 'text.primary' }}
              >
                {isSystem ? 'Lawis AI' : `${comment.user?.first_name} ${comment.user?.last_name}`}
              </Typography>
              {isSystem && (
                <Chip label="AI" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${goldAccent}20`, color: goldAccent, '& .MuiChip-label': { px: 0.5 } }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
              {formatRelativeTime(comment.timestamp, t)}
            </Typography>
          </Box>
        </Box>

        {/* Hide edit/delete for system comments — only resolve is allowed */}
        <CommentCardActions
          isOwner={isSystem ? false : isOwner}
          resolved={comment.resolved}
          onResolve={onResolve}
          onEdit={isSystem ? undefined : handleStartEdit}
          onDelete={isSystem ? undefined : onDelete}
        />
      </Box>

      {/* Context quoted text */}
      {comment.contextText && (
        <Box
          sx={{
            mb: 0.75,
            px: 1,
            py: 0.5,
            bgcolor: 'warning.50',
            borderLeft: '2px solid',
            borderColor: 'warning.main',
            borderRadius: '0 4px 4px 0',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
              wordBreak: 'break-word',
              fontSize: '0.72rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            "{comment.contextText}"
          </Typography>
        </Box>
      )}

      {/* Comment body or inline edit */}
      {isEditing ? (
        <Box sx={{ mb: 0.75 }}>
          <TextField
            fullWidth
            multiline
            size="small"
            variant="outlined"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            autoFocus
            sx={{
              mb: 0.75,
              '& .MuiOutlinedInput-root': { fontSize: '0.8rem', borderRadius: '6px' },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
            <Button
              size="small"
              onClick={() => setIsEditing(false)}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 1 }}
            >
              {t('cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={!editText.trim()}
              onClick={handleSubmitEdit}
              sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', px: 1.5 }}
            >
              {t('save')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            fontSize: '0.82rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: comment.resolved ? 'text.disabled' : 'text.primary',
            lineHeight: 1.55,
          }}
        >
          {comment.text}
        </Typography>
      )}

      {/* Replies */}
      {(comment.replies || []).length > 0 && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75, mb: 0.75 }}>
          {(comment.replies || []).map((reply) => (
            <Box key={reply.id} sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
              <Avatar
                sx={{
                  width: 20,
                  height: 20,
                  fontSize: '0.58rem',
                  bgcolor: reply.user?.color || 'primary.main',
                  flexShrink: 0,
                  mt: 0.2,
                }}
              >
                {getInitials(reply.user?.first_name, reply.user?.last_name)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography component="span" variant="caption" sx={{ fontWeight: 700, mr: 0.5, fontSize: '0.72rem' }}>
                  {reply.user?.first_name}
                </Typography>
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-word', fontSize: '0.72rem' }}>
                  {reply.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Reply input */}
      {!comment.resolved && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.25,
            py: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '20px',
            bgcolor: 'action.hover',
          }}
        >
          <InputBase
            fullWidth
            placeholder={t('reply_input_placeholder')}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleReplySubmit();
              }
            }}
            sx={{ fontSize: '0.76rem', color: 'text.secondary' }}
          />
        </Box>
      )}
    </Paper>
  );
};

export { getInitials, formatRelativeTime };
export default CommentCard;
